import React, { useState } from 'react';
import { ChevronDown, HelpCircle, ArrowRight, Plus, Minus } from 'lucide-react';
import { faqsData } from '../../data/faqsData';

interface FaqSectionProps {
  onOpenConsultation: () => void;
}

export const FaqSection: React.FC<FaqSectionProps> = ({ onOpenConsultation }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState<boolean>(false);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Initially show only the first 3 FAQs for quick scrolling
  const visibleFaqs = showAll ? faqsData : faqsData.slice(0, 3);

  return (
    <section id="faq-section" className="bg-[#071426] py-14 sm:py-16 border-b border-[#1b2f4a] relative">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Heading - Compact */}
        <div className="text-center mb-8 space-y-2">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-6 bg-[#FF851B]" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#FF851B]">
              FREQUENTLY ASKED QUESTIONS
            </span>
            <span className="h-px w-6 bg-[#FF851B]" />
          </div>

          <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white font-display">
            Clear Answers to Common Questions
          </h2>

          <p className="text-[#8FA0B8] text-xs sm:text-sm max-w-xl mx-auto">
            Everything you need to know about our engineering standards, delivery process, and engagement models.
          </p>
        </div>

        {/* Compact Accordion List */}
        <div className="space-y-2.5">
          {visibleFaqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] overflow-hidden transition-all duration-200"
              >
                <button
                  type="button"
                  id={`faq-btn-${idx}`}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${idx}`}
                  onClick={() => toggleAccordion(idx)}
                  className="w-full text-left px-4 py-3.5 sm:px-5 sm:py-4 flex items-center justify-between gap-3 focus:outline-none hover:bg-[#10223b]/60 transition-colors"
                >
                  <span className="text-xs sm:text-sm font-semibold text-white">
                    {faq.question}
                  </span>
                  <div className={`p-1 rounded-md bg-[#050A13] border border-[#1b2f4a] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#FF851B]' : 'text-[#8FA0B8]'}`}>
                    <ChevronDown size={14} />
                  </div>
                </button>

                {isOpen && (
                  <div
                    id={`faq-panel-${idx}`}
                    role="region"
                    aria-labelledby={`faq-btn-${idx}`}
                    className="px-4 pb-4 pt-1 sm:px-5 sm:pb-4 text-xs sm:text-sm text-[#D5DFEC] leading-relaxed border-t border-[#1b2f4a]/40 animate-fade-in"
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Show More / Show Less Toggle Button */}
        {faqsData.length > 3 && (
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0c1a2d] hover:bg-[#10223b] border border-[#1b2f4a] hover:border-[#159DE8]/50 text-xs font-semibold text-white transition-all shadow-sm"
            >
              {showAll ? (
                <>
                  <Minus size={13} className="text-[#FF851B]" />
                  <span>Show Fewer Questions</span>
                </>
              ) : (
                <>
                  <Plus size={13} className="text-[#159DE8]" />
                  <span>Show More Questions ({faqsData.length - 3} more)</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Additional Help Callout - Compact */}
        <div className="mt-8 p-4 rounded-xl bg-[#050A13] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#0B2342] text-[#159DE8] shrink-0 hidden sm:block">
              <HelpCircle size={18} />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-white">Have a specific technical question?</h4>
              <p className="text-[11px] text-[#8FA0B8]">Our senior technical architects are ready to assist you directly.</p>
            </div>
          </div>

          <button
            onClick={onOpenConsultation}
            className="px-4 py-2 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-semibold hover:shadow-glow-orange transition-all shrink-0 flex items-center gap-1.5"
          >
            <span>Ask an Architect</span>
            <ArrowRight size={13} />
          </button>
        </div>

      </div>
    </section>
  );
};
